import Button from 'components/crm/ui/Button';
import RichSelect from 'components/crm/ui/RichSelect';
import {
  COMPANY_ROLE_VALUES,
  companyRoleDescription,
  companyRoleLabel,
} from 'constants/companyRoles';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ApiDatabase from 'server';
import { errorCodeOf } from 'helper/apiErrorText';

// The refusals of admin/postEditCompanyMember worded under record.roleErrors;
// any other is the generic one.
const ROLE_ERROR_CODES = [
  'ADMIN_ROLE_TAKEN',
  'ROLE_INVALID',
  'MEMBER_NOT_FOUND',
];

/**
 * « Modifier le rôle » of a member with an account: one of the company roles
 * (constants/companyRoles.js), sent with the member's own names and e-mail
 * (admin/postEditCompanyMember wants the whole employee). A refusal stays in
 * the window, worded by its errorCode; `onSaved` once the api took it.
 */
const MemberRoleDialog = ({ companyId, member, onClose, onSaved }) => {
  const { text, language } = useText('record');
  const [role, setRole] = useState(Number(member.role) || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Listened to in the bubble phase: an open role list takes Escape first
  // (capture phase, marked handled) and closes alone.
  useEffect(() => {
    const onKeyDown = (event) =>
      event.key === 'Escape' && !event.defaultPrevented && closeRef.current();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
  const name = [member.firstname, member.lastname].filter(Boolean).join(' ');

  const save = () => {
    if (busy || !role) return;
    if (role === Number(member.role)) {
      onClose();
      return;
    }
    setBusy(true);
    setError('');
    ApiDatabase.postEditCompanyMember(
      {
        idCompany: companyId,
        idEmployee: member.id,
        employee: {
          firstName: member.firstname,
          lastName: member.lastname,
          email: member.email,
          role,
        },
      },
      () => {
        setBusy(false);
        onSaved(role);
      },
      (failure) => {
        setBusy(false);
        if (failure?.name === 'CanceledError') return;
        const code = errorCodeOf(failure);
        setError(
          text(
            `roleErrors.${ROLE_ERROR_CODES.includes(code) ? code : 'generic'}`,
          ),
        );
      },
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-role-title"
        className="flex w-[440px] max-w-full flex-col gap-4 rounded-2xl bg-white p-6 font-crm text-body shadow-pop"
        onClick={(event) => event.stopPropagation()}
      >
        <h2
          id="member-role-title"
          className="font-title text-xl font-semibold text-ink"
        >
          {text('roleTitle', { name })}
        </h2>
        <RichSelect
          id="member-role"
          label={text('roleLabel')}
          value={role}
          options={COMPANY_ROLE_VALUES.map((value) => ({
            value,
            label: companyRoleLabel(language, value),
            description: companyRoleDescription(language, value),
          }))}
          onChange={(next) => {
            setRole(next);
            setError('');
          }}
        />
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {text('roleCancel')}
          </Button>
          <Button
            disabled={busy || !role}
            aria-busy={busy || undefined}
            onClick={save}
          >
            {text('roleSave')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MemberRoleDialog;
